import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import Typography from '@mui/material/Typography';

export const drawerWidth = 340;

const Main = styled('main', { shouldForwardProp: prop => prop !== 'open' })(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0,
  transition: theme.transitions.create('margin', {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp,
  }),
  width: '100%',
  ...(open && {
    transition: theme.transitions.create('margin', {
      duration: theme.transitions.duration.enteringScreen,
      easing: theme.transitions.easing.easeOut,
    }),
    width: `calc(100% - ${drawerWidth}px)`,
  }),
}));

function FilterableContent({
  canToggleFilterDrawer,
  children,
  titleMenu,
  filterContent,
  headerTitle,
  onApply,
  handleReset,
  hasChanges,
  isOnDefaultState,
  isExporting,
  onSearchChange,
  searchPlaceholder,
  searchValue,
  showQuickSearch,
}) {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('mdOpen'), { noSsr: true });
  const [open, setOpen] = React.useState(mdUp);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Debounce search: auto-apply filters after user stops typing (immediate for clears)
  React.useEffect(() => {
    if (isExporting) return undefined;
    const delay = searchValue ? 1000 : 0;
    const timeoutId = setTimeout(() => {
      onApply();
    }, delay);

    // Cleanup timeout if searchValue changes before delay
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleClearSearch = () => {
    onSearchChange('');
  };

  return (
    <Box sx={{ display: 'flex', maxWidth: '100vw', mt: 6, overflowX: 'hidden', width: '100%' }}>
      <Drawer
        anchor="left"
        open={open}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '_next.surface.white',
              boxShadow: 'none',
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'content-box',
            position: 'relative',
          },
          flexShrink: 0,
          width: open ? drawerWidth : 0,
          zIndex: 100,
        }}
        variant="persistent"
      >
        <Box sx={{ margin: 0, p: 0 }}>
          <Stack spacing={3}>
            {/* Filters Go here */}
            {filterContent}
          </Stack>
        </Box>
        <Box
          sx={{
            backgroundColor: 'surface.white',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            mt: 'auto',
            px: 4,
            py: 2,
          }}
        >
          <Button
            disabled={isOnDefaultState || isExporting}
            fullWidth
            onClick={handleReset}
            variant="text-blue"
          >
            Reset
          </Button>
          <Button
            disabled={!hasChanges || isExporting}
            fullWidth
            onClick={onApply}
            variant="outlined-blue"
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
      <Main
        open={open}
        sm={6}
        sx={{
          justifyContent: 'space-between',
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        {/* Filter button and search bar */}
        <Box
          alignItems="center"
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          sx={{
            margin: '0 auto',
            p: 3,
            pb: 2,
            width: '100%',
          }}
        >
          <Typography sx={{ color: '_next.text.primary' }} variant="h6">
            {headerTitle}
          </Typography>
          {/* TitleMenu Buttons Go Here */}
          {titleMenu && (
            <Box display="flex" gap={1}>
              {titleMenu}
            </Box>
          )}
        </Box>
        <Box
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          sx={{
            margin: '0 auto',
            mb: 0,
            px: 3,
            width: '100%',
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            divider={<Divider flexItem orientation="vertical" />}
            justifyContent="center"
            spacing={1}
          >
            {canToggleFilterDrawer && (
              <Button
                disabled={isExporting}
                onClick={toggleDrawer}
                startIcon={<TuneRoundedIcon />}
                variant="vertical-with-icon-outlined"
              >
                Filter
              </Button>
            )}
            {showQuickSearch && (
              <TextField
                disabled={isExporting}
                id="outlined-basic"
                label="Search"
                onChange={e => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                // TODO: we should eventually move this to the theme
                slotProps={{
                  input: {
                    endAdornment: searchValue ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          disabled={isExporting}
                          edge="end"
                          onClick={handleClearSearch}
                          size="small"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& input': {
                      padding: '14px 4px',
                    },
                  },
                  minWidth: '240px',
                }}
                value={searchValue}
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
        {/* Content Wrapper */}
        <Box
          sx={{
            backgroundColor: () => theme.palette._next.surface.light,
            height: 'calc(100vh - 167px)',
            justifyContent: 'flex-start',
            mb: 0,
            overflow: 'scroll',
            width: '100%',
          }}
        >
          {children}
        </Box>
      </Main>
    </Box>
  );
}

FilterableContent.propTypes = {
  canToggleFilterDrawer: PropTypes.bool,
  children: PropTypes.node.isRequired,
  filterContent: PropTypes.node.isRequired,
  handleReset: PropTypes.func,
  hasChanges: PropTypes.bool,
  headerTitle: PropTypes.string.isRequired,
  isExporting: PropTypes.bool,
  isOnDefaultState: PropTypes.bool,
  onApply: PropTypes.func,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  searchValue: PropTypes.string,
  showQuickSearch: PropTypes.bool,
  titleMenu: PropTypes.node,
};
FilterableContent.defaultProps = {
  canToggleFilterDrawer: true,
  handleReset: () => {},
  hasChanges: false,
  isExporting: false,
  isOnDefaultState: true,
  onApply: () => {},
  onSearchChange: () => {},
  searchPlaceholder: 'Search content',
  searchValue: '',
  showQuickSearch: true,
  titleMenu: null,
};

export default FilterableContent;
